<template>
  <v-sheet class="w-100">
    <v-toolbar>
      <v-toolbar-title>{{ $t('common.actions.inviteUser') }}</v-toolbar-title>
      <v-spacer></v-spacer>
    </v-toolbar>
    <v-container class="user-form mt-4">
      <v-form ref="form">
        <v-row>
          <v-col cols="8" offset="2">
            <v-text-field
              v-model="formUser.nickname"
              :label="$t('domain.fields.username')"
              variant="outlined"
              autofocus
              :rules="rules.nickname"></v-text-field>
          </v-col>
          <v-col cols="8" offset="2">
            <v-text-field
              v-model="formUser.email"
              :label="$t('domain.fields.email')"
              variant="outlined"
              :rules="rules.email"
              :error-messages="emailErrorMessages"
              @update:model-value="onEmailInput"></v-text-field>
          </v-col>
          <v-col cols="8" offset="2">
            <v-text-field
              v-model="formUser.given_name"
              :label="$t('domain.fields.firstName')"
              variant="outlined"
              :rules="rules.given_name"></v-text-field>
          </v-col>
          <v-col cols="8" offset="2">
            <v-text-field
              v-model="formUser.family_name"
              :label="$t('domain.fields.lastName')"
              variant="outlined"
              :rules="rules.family_name"></v-text-field>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
    <v-container>
      <div class="text-right ma-2">
        <v-btn color="rgb(var(--v-theme-background))" size="small" class="mr-2" variant="flat" @click="emits('cancel')">
          {{ $t('common.actions.cancel') }}
        </v-btn>
        <v-btn v-if="!props.loading" color="primary" size="small" variant="flat" @click="save">{{ $t('common.actions.inviteUser') }}</v-btn>
        <v-progress-circular v-else indeterminate color="primary"></v-progress-circular>
      </div>
    </v-container>
  </v-sheet>
</template>
<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed, ref } from 'vue'

import { cloneDeep } from 'lodash'
import { useI18n } from 'vue-i18n'

import { isRequired, isValidEmail, validate, applyRules } from '@tsyl/radon'

import type { User } from '../../types/User'
const { t } = useI18n()

// Composables

const props = defineProps({
  user: {
    type: Object as PropType<User>,
    default: () => ({})
  },
  loading: {
    type: Boolean,
    default: false
  },
  emailError: {
    type: String,
    default: ''
  }
})

const emits = defineEmits<{
  (e: 'save', v: User): void
  (e: 'cancel', v: void): void
  (e: 'update:emailError', v: string): void
}>()

const emailErrorMessages = computed(() => (props.emailError ? [props.emailError] : []))

function onEmailInput() {
  if (props.emailError) emits('update:emailError', '')
}

const form = ref()

const rules = computed(() => {
  return {
    nickname: validate(t('domain.fields.username'), isRequired()),
    email: validate(t('domain.fields.email'), isRequired(), isValidEmail()),
    given_name: validate(t('domain.fields.firstName'), isRequired()),
    family_name: validate(t('domain.fields.lastName'), isRequired())
  }
})

const formUser = ref(cloneDeep(props.user))

const save = () => {
  const values = {
    ...formUser.value
  }
  const valid = applyRules(rules.value, {
    ...values
  })
  form.value.validate()

  if (valid === true) emits('save', values)
}
</script>

<style scoped lang="scss"></style>
